import { useMemo, useState } from 'react';
import { messagePreview, messagesOfTask, uuidv7, type Priority, type Task } from '@machs/shared';
import { useSync } from '../lib/sync';
import { usePlan } from '../lib/plan';
import { api, userAvatarUrl } from '../lib/api';
import { Icon } from '../lib/icons';
import { avatarGradient, initials, shortDate } from '../lib/format';
import { Avatar, useToast } from './ui';
import { ChecklistSection, SubtaskSection } from './TaskStructure';

const PRIORITIES: { id: Priority; label: string }[] = [
  { id: 'niedrig', label: 'Niedrig' },
  { id: 'normal', label: 'Normal' },
  { id: 'hoch', label: 'Hoch' },
];

function isoDay(offsetDays: number): string {
  const d = new Date();
  d.setHours(17, 0, 0, 0);
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString();
}

export function DetailPanel({ task, onClose, onShare, onEditProject, onOpenTask }: {
  task: Task;
  onClose: () => void;
  /** Unter- oder Hauptaufgabe öffnen */
  onOpenTask: (id: string) => void;
  onShare?: () => void;
  /** Projekt der Aufgabe bearbeiten (aus dem früheren Menü im Kopf) */
  onEditProject?: (projectId: string) => void;
}) {
  const { client, me, version } = useSync();
  const state = client.data.taskUserState.get(`${task.id}:${me.user.id}`);
  const pinned = state?.pinned ?? false;
  const muted = state?.muted ?? false;
  // Eigene Markierungen, in der Reihenfolge des Verlaufs
  const starredMessages = useMemo(
    () => messagesOfTask(client, task.id).filter((m) => !m.deletedAt && state?.starred?.includes(m.id)),
    [client, version, task.id, state],
  );
  const [newCategory, setNewCategory] = useState('');

  const timeline = useMemo(() => {
    return messagesOfTask(client, task.id)
      .filter((m) => m.isMilestone)
      .map((m) => ({
        id: m.id,
        color: 'var(--milestone)',
        title: `Meilenstein${m.kind === 'meilenstein' ? `: ${m.body}` : ''}`,
        sub: `${shortDate(m.createdAt)}, ${m.authorName}`,
      }));
  }, [client, version, task.id]);

  const save = (patch: Partial<Task>) =>
    void client.saveTask({
      id: task.id,
      projectId: task.projectId,
      title: task.title,
      ...(patch.priority !== undefined && { priority: patch.priority }),
      ...(patch.dueAt !== undefined && { dueAt: patch.dueAt }),
      ...(patch.assigneeIds !== undefined && { assigneeIds: patch.assigneeIds }),
      ...(patch.categoryIds !== undefined && { categoryIds: patch.categoryIds }),
    });

  const categories = [...client.data.category.values()].sort((a, b) => a.name.localeCompare(b.name, 'de'));

  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  return (
    <div className="scroll flex h-full flex-col gap-6 p-5">
      <div className="flex items-center justify-between">
        <h3 className="m-0 text-[15px] font-semibold">Details</h3>
        <button className="iconbtn plain" onClick={onClose} aria-label="Details einklappen" data-tip="Einklappen" data-tip-below=""><Icon name="x" size={18} /></button>
      </div>

      {/* Was früher im Menü im Kopf stand */}
      <div className="flex flex-wrap gap-2">
        <button className="chip" aria-pressed={pinned} onClick={() => void client.setTaskUserState(task.id, { pinned: !pinned })}>
          <Icon name="pin" size={14} />{pinned ? 'Oben gehalten' : 'Oben halten'}
        </button>
        <button
          className="chip"
          aria-pressed={muted}
          onClick={() => void client.setTaskUserState(task.id, { muted: !muted })}
          data-tip={muted ? 'Nur noch, was dich direkt betrifft' : 'Keine Benachrichtigungen für neue Nachrichten'}
          data-tip-wrap
        >
          <Icon name="bell" size={14} />{muted ? 'Stummgeschaltet' : 'Stummschalten'}
        </button>
        {onShare && (
          <button className="chip" onClick={onShare}><Icon name="share" size={14} />Externe einladen</button>
        )}
        {onEditProject && (
          <button className="chip" onClick={() => onEditProject(task.projectId)}><Icon name="gear" size={14} />Projekt</button>
        )}
      </div>

      <SubtaskSection task={task} onOpenTask={onOpenTask} />
      <ChecklistSection task={task} />

      <AiSummary task={task} />

      {starredMessages.length > 0 && (
        <section>
          <h4 className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold text-[var(--muted)]"><Icon name="starFill" size={13} className="text-[var(--milestone)]" />Markiert</h4>
          <ul className="starlist">
            {starredMessages.map((m) => (
              <li key={m.id}>
                <button onClick={() => springeZu(m.id)}>
                  <b className="block truncate text-[13px]">{m.body.trim() || messagePreview(m.kind, '', client.data.media.get(m.mediaId ?? '') ?? null)}</b>
                  <small className="text-xs text-[var(--muted)]">{m.authorName} · {shortDate(m.createdAt)}</small>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h4 className="mb-2 text-[13px] font-semibold text-[var(--muted)]">Verlauf</h4>
        {timeline.length === 0 ? (
          <p className="m-0 text-[13.5px] text-[var(--muted)]">Noch keine Meilensteine. Über das Plus lässt sich ein erreichter Stand festhalten.</p>
        ) : (
          <ol className="relative m-0 list-none p-0 before:absolute before:top-1.5 before:bottom-1.5 before:left-[10px] before:w-0.5 before:bg-[var(--line)]">
            {timeline.map((t) => (
              <li key={t.id} className="relative pb-3 pl-8 text-[13.5px]">
                <span className="absolute top-[3px] left-[3px] h-4 w-4 rounded-full border-[3px] border-[var(--bg)]" style={{ background: t.color }} />
                <b className="block font-semibold">{t.title}</b>
                <small className="text-xs text-[var(--muted)]">{t.sub}</small>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section>
        <h4 className="mb-2 text-[13px] font-semibold text-[var(--muted)]">Zuständig</h4>
        <div className="flex flex-col gap-1">
          {me.members.map((m) => {
            const on = task.assigneeIds.includes(m.userId);
            return (
              <button key={m.userId} className="row !py-1.5" onClick={() => save({ assigneeIds: toggle(task.assigneeIds, m.userId) })} aria-pressed={on}>
                <Avatar name={initials(m.name)} gradient={avatarGradient(m.userId)} src={userAvatarUrl(m.userId, m.avatarAt)} size={30} />
                <span className="flex-1 text-[14px]">{m.name}{m.userId === me.user.id ? ' (du)' : ''}</span>
                {on && <Icon name="check" size={18} className="text-[var(--brand-a)]" />}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <h4 className="mb-2 text-[13px] font-semibold text-[var(--muted)]">Fällig{task.dueAt ? ` am ${shortDate(task.dueAt)}` : ''}</h4>
        <div className="chips flex-wrap">
          <button className="chip" onClick={() => save({ dueAt: isoDay(0) })}>Heute</button>
          <button className="chip" onClick={() => save({ dueAt: isoDay(1) })}>Morgen</button>
          <button className="chip" onClick={() => save({ dueAt: isoDay(7) })}>In einer Woche</button>
          {task.dueAt && <button className="chip" onClick={() => save({ dueAt: null })}>Kein Termin</button>}
        </div>
        <input
          type="date"
          className="input mt-2 w-full"
          value={task.dueAt ? task.dueAt.slice(0, 10) : ''}
          onChange={(e) => save({ dueAt: e.target.value ? new Date(`${e.target.value}T17:00:00`).toISOString() : null })}
          aria-label="Fälligkeit wählen"
        />
      </section>

      <section>
        <h4 className="mb-2 text-[13px] font-semibold text-[var(--muted)]">Priorität</h4>
        <div className="chips">
          {PRIORITIES.map((p) => (
            <button key={p.id} className="chip" aria-pressed={task.priority === p.id} onClick={() => save({ priority: p.id })}>{p.label}</button>
          ))}
        </div>
      </section>

      <section>
        <h4 className="mb-2 text-[13px] font-semibold text-[var(--muted)]">Kategorien</h4>
        <div className="chips flex-wrap">
          {categories.map((c) => (
            <button key={c.id} className="chip" aria-pressed={task.categoryIds.includes(c.id)} onClick={() => save({ categoryIds: toggle(task.categoryIds, c.id) })}>{c.name}</button>
          ))}
        </div>
        <form
          className="mt-2 flex gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            const name = newCategory.trim();
            if (!name) return;
            const id = uuidv7();
            await client.saveCategory({ id, name });
            save({ categoryIds: [...task.categoryIds, id] });
            setNewCategory('');
          }}
        >
          <input className="input h-10 min-w-0 flex-1" placeholder="Neue Kategorie" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
          <button className="iconbtn h-10 w-10" aria-label="Kategorie anlegen"><Icon name="plus" size={18} /></button>
        </form>
      </section>

      <section>
        <h4 className="mb-2 text-[13px] font-semibold text-[var(--muted)]">Freigabe</h4>
        <div className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-3 text-[13px] text-[var(--muted)]">
          <b className="mb-1 flex items-center gap-1.5 text-[var(--text)]"><Icon name="share" size={16} className="text-[var(--brand-a)]" />Per Link teilen</b>
          Externe sehen die Aufgabe ohne Konto im Browser und schreiben mit.
          {onShare && <button className="btn btn-ghost mt-2 h-9 w-full" onClick={onShare}>Externe einladen</button>}
        </div>
      </section>
    </div>
  );
}

interface Summary {
  summary: string;
  status: string;
  openPoints: string[];
  suggestions: { text: string; due: string | null }[];
  model: string;
  createdAt: string;
}

/** KI-Zusammenfassung der ganzen Aufgabe mit offenen Punkten und Vorschlägen. */
function AiSummary({ task }: { task: Task }) {
  const { client } = useSync();
  const plan = usePlan();
  const toast = useToast();
  const [state, setState] = useState<Summary | null>(null);
  const [busy, setBusy] = useState(false);

  async function run() {
    setBusy(true);
    try {
      setState(await api<Summary>(`/ai/tasks/${task.id}/summary`, { method: 'POST' }));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Zusammenfassung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  async function asTask(text: string, due: string | null) {
    if (!plan.more('openTasks')) return;
    // Offene Punkte gehören zur Aufgabe: als Unteraufgabe (bei einer Unteraufgabe daneben)
    const id = await client.saveTask({
      projectId: task.projectId,
      title: text.slice(0, 200),
      dueAt: due ? new Date(`${due}T17:00:00`).toISOString() : null,
      parentId: task.parentId ?? task.id,
    });
    await client.sendMessage({ taskId: id, kind: 'text', body: `${text}\n\nVorschlag aus „${task.title}“` });
    toast('Unteraufgabe angelegt');
  }

  return (
    <section className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-3">
      {/* Knopf rutscht in die nächste Zeile, statt den Titel zu überlagern */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <Icon name="sparkle" size={16} className="flex-none text-[var(--brand-a)]" />
        <b className="min-w-0 flex-1 truncate text-[14px]">KI-Zusammenfassung</b>
        <button className="chip flex-none" onClick={() => void run()} disabled={busy}>{busy ? 'Denkt nach …' : state ? 'Neu' : 'Erstellen'}</button>
      </div>
      {!state && !busy && <p className="m-0 mt-1.5 text-[13px] text-[var(--muted)]">Fasst den Verlauf zusammen und zeigt, was noch offen ist.</p>}
      {busy && !state && <div className="mt-3 grid place-items-center"><div className="spinner" /></div>}
      {state && (
        <div className="mt-2 flex flex-col gap-2 text-[13.5px]">
          <p className="m-0">{state.summary}</p>
          <p className="m-0 text-[var(--muted)]"><b className="text-[var(--text)]">Stand:</b> {state.status}</p>
          {state.openPoints.length > 0 && (
            <div>
              <b className="text-[13px]">Offen</b>
              <ul className="m-0 mt-1 list-disc pl-5">
                {state.openPoints.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}
          {state.suggestions.length > 0 && (
            <div>
              <b className="text-[13px]">Nächste Schritte</b>
              <ul className="m-0 mt-1 flex list-none flex-col gap-1 p-0">
                {state.suggestions.map((sg, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="flex-1">{sg.text}{sg.due ? <span className="text-[var(--muted)]"> (bis {shortDate(sg.due)})</span> : null}</span>
                    <button className="iconbtn" style={{ width: 26, height: 26, borderRadius: 8 }} onClick={() => void asTask(sg.text, sg.due)} data-tip="Als Aufgabe anlegen" aria-label="Als Aufgabe anlegen">
                      <Icon name="plus" size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <small className="text-[11.5px] text-[var(--dim)]">Erstellt mit {state.model}. Bitte prüfen, KI kann sich irren.</small>
        </div>
      )}
    </section>
  );
}

/** Zur Nachricht im Chat blättern und sie kurz hervorheben. */
function springeZu(id: string) {
  const el = document.querySelector(`[data-msg="${id}"]`);
  if (!el) return;
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  el.classList.add('flash');
  setTimeout(() => el.classList.remove('flash'), 1600);
}
